import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";

/**
 * Iter-164 · Runs once on mount + on every org switch: takes the org's
 * brand_color hex and injects it as the `--accent` HSL Tailwind CSS variable,
 * so every accent-tinted class in the portal (buttons, links, focus rings,
 * bg-accent/*, text-accent) instantly re-brands.
 */
function hexToHslString(hex) {
  if (!hex || typeof hex !== "string") return null;
  const h = hex.replace("#", "");
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(h)) return null;
  const bigint = parseInt(h.length === 3 ? h.split("").map(c => c+c).join("") : h, 16);
  const r = ((bigint >> 16) & 255) / 255;
  const g = ((bigint >> 8) & 255) / 255;
  const b = (bigint & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hh = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hh = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hh = (b - r) / d + 2; break;
      case b: hh = (r - g) / d + 4; break;
      default: break;
    }
    hh /= 6;
  }
  return `${Math.round(hh * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export default function BrandThemeApplier() {
  const { org } = useAuth();
  useEffect(() => {
    const hsl = hexToHslString(org?.brand_color);
    if (hsl) {
      document.documentElement.style.setProperty("--accent", hsl);
      document.documentElement.style.setProperty("--ring", hsl);
    } else {
      document.documentElement.style.removeProperty("--accent");
      document.documentElement.style.removeProperty("--ring");
    }
  }, [org?.brand_color, org?.id]);
  return null;
}
